import { useEffect, useState } from "react";
import { ArrowUpRight, Star } from "lucide-react";

export default function ReviewsCarousel({ reviews, googleUrl }) {
  const [index, setIndex] = useState(0);
  const [animate, setAnimate] = useState(true);
  const [paused, setPaused] = useState(false);
  const [perView, setPerView] = useState(() => (window.innerWidth >= 1024 ? 3 : 1));

  const track = [...reviews, ...reviews];
  const basis = 100 / perView;

  useEffect(() => {
    const onResize = () => setPerView(window.innerWidth >= 1024 ? 3 : 1);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setIndex((i) => i + 1), 4000);
    return () => clearInterval(t);
  }, [paused]);

  useEffect(() => {
    if (index >= reviews.length) {
      const t = setTimeout(() => {
        setAnimate(false);
        setIndex(0);
        requestAnimationFrame(() => requestAnimationFrame(() => setAnimate(true)));
      }, 650);
      return () => clearTimeout(t);
    }
  }, [index, reviews.length]);

  return (
    <div
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="reviews-carousel"
    >
      <div className="overflow-hidden">
        <div
          className="flex"
          style={{
            transform: `translateX(-${index * basis}%)`,
            transition: animate ? "transform 0.6s cubic-bezier(0.22, 1, 0.36, 1)" : "none",
          }}
        >
          {track.map((r, i) => (
            <div key={i} className="flex-shrink-0 px-2" style={{ width: `${basis}%` }}>
              <figure className="flex h-full flex-col rounded-2xl bg-white p-7 text-brand-ink" data-testid={`review-${i % reviews.length}`}>
                <div className="flex gap-0.5">
                  {Array.from({ length: 5 }).map((_, j) => (
                    <Star key={j} className="h-4 w-4 fill-brand-lime text-brand-lime" />
                  ))}
                </div>
                <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-neutral-600">"{r.text}"</blockquote>
                <figcaption className="mt-5 border-t border-neutral-100 pt-4">
                  <p className="text-sm font-bold">{r.name}</p>
                  <a
                    href={googleUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-0.5 flex items-center gap-1 text-[11px] font-semibold text-neutral-400 hover:text-brand-red"
                  >
                    Verified on Google <ArrowUpRight className="h-3 w-3" />
                  </a>
                </figcaption>
              </figure>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-6 flex justify-center gap-2">
        {reviews.map((_, i) => (
          <button
            key={i}
            onClick={() => setIndex(i)}
            aria-label={`Go to review ${i + 1}`}
            data-testid={`review-dot-${i}`}
            className={`h-1.5 rounded-full transition-[width,background-color] duration-300 ${
              index % reviews.length === i ? "w-6 bg-brand-lime" : "w-1.5 bg-white/25 hover:bg-white/50"
            }`}
          />
        ))}
      </div>
    </div>
  );
}
